import React, { useState, useEffect } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { useAuth } from '../../shared/auth'
import { MessageDialog } from '../../shared/ui'
import { useApiError } from '../../shared/hooks'
import toast from 'react-hot-toast'
import logo from '/core/img/logo.png'

const Login = () => {
  const navigate = useNavigate()
  const { login, error, dispatch } = useAuth()
  const [isLoading, setIsLoading] = useState(false)
  
  // Use the new error handling hook
  const { 
    showErrorDialog, 
    errorMessage, 
    handleApiError, 
    handleValidationErrors, 
    closeErrorDialog 
  } = useApiError(() => setIsLoading(false))

  const {
    register,
    handleSubmit,
  } = useForm()

  // Browser compatibility check (similar to original jQuery code)
  const browserCheck = () => {
    const agent = window.navigator.userAgent.toLowerCase()
    const ua = navigator.userAgent

    // Mobile check
    if (ua.indexOf('iPhone') > 0 || (ua.indexOf('Android') > 0 && ua.indexOf('Mobile') > 0)) {
      handleValidationErrors('pix-storageはPC版のGoogle Chromeのみ使用可能です。')
      return false
    }
    
    // Tablet check
    if (ua.indexOf('iPad') > 0 || ua.indexOf('Android') > 0) {
      handleValidationErrors('pix-storageはPC版のGoogle Chromeのみ使用可能です。')
      return false
    }
    
    // PC browser check
    if (agent.indexOf('msie') > -1 || agent.indexOf('trident') > -1 || agent.indexOf('edge') > -1) {
      handleValidationErrors('pix-storageはPC版のGoogle Chromeのみ使用可能です。')
      return false
    }
    
    if (agent.indexOf('chrome') > -1) {
      return true
    } else {
      handleValidationErrors('pix-storageはPC版のGoogle Chromeのみ使用可能です。')
      return false
    }
  }

  // Keyboard navigation handlers
  const handleCompanyIdKeyDown = (e) => {
    if (e.keyCode === 13) { // Enter key
      e.preventDefault()
      document.getElementById('mailAddress').focus()
    }
  }

  const handleMailAddressKeyDown = (e) => {
    if (e.keyCode === 13) { // Enter key
      e.preventDefault()
      document.getElementById('password').focus()
    }
  }

  const onSubmit = async (data) => {
    // Check browser compatibility first
    if (!browserCheck()) {
      return
    }

    // Validate required fields (like original app)
    if (!data.companyLoginId || data.companyLoginId.trim() === '') {
      handleValidationErrors('企業IDを入力してください。')
      return
    }

    if (!data.mailAddress || data.mailAddress.trim() === '') {
      handleValidationErrors('メールアドレスを入力してください。')
      return
    }

    if (!data.password || data.password.trim() === '') {
      handleValidationErrors('パスワードを入力してください。')
      return
    }

    setIsLoading(true)
    try {
      await login(data) // Error handling is done in the login function
      toast.success('ログインに成功しました')
      navigate('/top') // Redirect to top page like original app
    } catch (error) {
      handleApiError(error, error.message || 'ログインに失敗しました。認証情報を確認してください。')
    } finally {
      setIsLoading(false)
    }
  }

  useEffect(() => {
    if (error) {
      // setPopupError(error.message)
      handleValidationErrors(error || 'エラーが発生しました。')
      dispatch({ type: 'CLEAR_ERROR' })
    }
  }, [error, handleValidationErrors, dispatch])

  return (
    <div className="min-h-screen bg-gray-100 flex items-center justify-center p-4">
      <div className="login-form-area">
        {/* Logo */}
        <div className="text-center mb-8">
          <img src={logo} alt="pix-storage" className="mx-auto mb-4" />
        </div>

        {/* Login Form */}
        <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
          {/* Hidden fields for compatibility with original app */}
          <input type="hidden" id="token" name="token" value="" />
          <input type="hidden" id="tokenCreateTime" name="tokenCreateTime" value="" />
          <input type="hidden" id="pixId" name="pixId" value="" />
          <input type="hidden" id="userId" name="userId" value="" />
          <input type="hidden" id="userKyotenId" name="userKyotenId" value="" />
          <input type="hidden" id="userAuthority" name="userAuthority" value="" />
          <input type="hidden" id="successFlg" name="successFlg" value="" />
          <input type="hidden" id="message" name="message" value="" />
          <input type="hidden" id="errMessage" name="errMessage" value="" />
          <input type="hidden" id="gamenMaskIdList" name="gamenMaskIdList" value="" />

          {/* 企業ID */}
          <div>
            <label htmlFor="companyLoginId" className="block text-gray-700 mb-2">企業ID</label>
            <input
              id="companyLoginId"
              type="text"
              {...register('companyLoginId')}
              className="login-form-input"
              placeholder="企業ID"
              onKeyDown={handleCompanyIdKeyDown}
            />
          </div>

          {/* メールアドレス */}
          <div>
            <label htmlFor="mailAddress" className="block text-gray-700 mb-2">メールアドレス</label>
            <input
              id="mailAddress"
              type="email"
              {...register('mailAddress')}
              className="login-form-input"
              placeholder="メールアドレス"
              onKeyDown={handleMailAddressKeyDown}
            />
          </div>

          {/* パスワード */}
          <div>
            <label htmlFor="password" className="block text-gray-700 mb-2">パスワード</label>
            <input
              id="password"
              type="password"
              {...register('password')}
              className="login-form-input"
              placeholder="パスワード"
            />
          </div>

          {/* ログインボタン */}
          <div className="text-center">
            <button
              type="submit"
              disabled={isLoading}
              className="login-button"
            >
              {isLoading ? 'ログイン中...' : 'ログイン'}
            </button>
          </div>

          {/* Footer Links */}
          <div className="flex justify-between mt-8">
            <button
              type="button"
              onClick={() => navigate('/riyoToroku')}
              className="bg-transparent border-none p-0 m-0 text-blue-600 underline cursor-pointer text-sm font-normal"
            >
              会員登録はこちら
            </button>
            <button
              type="button"
              onClick={() => navigate('/inquiry')}
              className="bg-transparent border-none p-0 m-0 text-blue-600 underline cursor-pointer text-sm font-normal"
            >
              お問い合わせ
            </button>
          </div>
        </form>
      </div>

      {/* Error Dialog */}
      <MessageDialog
        isOpen={showErrorDialog}
        onClose={closeErrorDialog}
        title="ログインエラー"
        message={errorMessage}
        type="error"
      />
    </div>
  )
}

export default Login
